/**
 * Klassiker Ersatzteile – Fahrzeug-Finder
 * Abhängige Auswahl Hersteller → Modell (+ Kategorie) und Schlüsselnummer (HSN/TSN).
 * Die Auswahllisten kommen aus den Storefront-Filtern (Search & Discovery) und werden
 * über die Section Rendering API (sections/fahrzeug-finder-daten.liquid) geladen.
 */
(function () {
  const DATA_SECTION = 'fahrzeug-finder-daten';
  const PARAMS = {
    hersteller: 'filter.p.m.custom.hersteller',
    modell: 'filter.p.m.custom.modell',
    hsn: 'filter.p.m.custom.hsn_tsn',
  };

  class KeFahrzeugFinder extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[data-ke-fahrzeug]');
      this.hsnForm = this.querySelector('form[data-ke-hsn]');
      this.selHersteller = this.querySelector('select[data-ke-hersteller]');
      this.selModell = this.querySelector('select[data-ke-modell]');
      this.selKategorie = this.querySelector('select[data-ke-kategorie]');
      this.submitBtn = this.querySelector('[data-ke-submit]');
      this.status = this.querySelector('[data-ke-status]');
      this.fallback = this.querySelector('[data-ke-fallback]');
      this.mainUi = this.querySelector('[data-ke-main]');
      this.hint = this.querySelector('[data-ke-hint]');
      this.chips = Array.from(this.querySelectorAll('[data-ke-marke]'));
      this.designMode = this.dataset.designMode === 'true';
      this.allHandle = this.dataset.allHandle || 'all';
      this.data = null;
      this.abort = null;

      if (this.form) {
        this.form.addEventListener('submit', (e) => this.onSubmit(e));
      }
      if (this.selHersteller) {
        this.selHersteller.addEventListener('change', () => this.onHerstellerChange());
      }
      if (this.selKategorie) {
        this.selKategorie.addEventListener('change', () => this.loadInitial(true));
      }
      if (this.hsnForm) {
        this.hsnForm.addEventListener('submit', (e) => this.onHsnSubmit(e));
      }
      this.loadInitial(false);
    }

    get baseUrl() {
      const handle = (this.selKategorie && this.selKategorie.value) || this.dataset.contextHandle || this.allHandle;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return `${root}collections/${handle}`.replace('//', '/');
    }

    async fetchData(params) {
      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      const url = new URL(this.baseUrl, window.location.origin);
      url.searchParams.set('section_id', DATA_SECTION);
      Object.entries(params || {}).forEach(([k, v]) => {
        if (v) url.searchParams.set(k, v);
      });
      const res = await fetch(url.toString(), {
        headers: { Accept: 'text/html' },
        signal: this.abort.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const m = html.match(/<script type="application\/json" data-ke-finder-data>([\s\S]*?)<\/script>/);
      if (!m) throw new Error('Keine Finder-Daten');
      return JSON.parse(m[1]);
    }

    setBusy(busy) {
      this.classList.toggle('ke-finder--busy', !!busy);
      if (this.submitBtn) this.submitBtn.toggleAttribute('aria-busy', !!busy);
    }

    fillSelect(select, values, placeholder, selectedValue) {
      if (!select) return;
      const current = selectedValue !== undefined ? selectedValue : select.value;
      select.innerHTML = '';
      const ph = document.createElement('option');
      ph.value = '';
      ph.textContent = placeholder;
      select.appendChild(ph);
      values.forEach((v) => {
        const opt = document.createElement('option');
        opt.value = v.value;
        opt.textContent = v.count > 0 ? `${v.label} (${v.count})` : v.label;
        if (v.value === current || v.active) opt.selected = true;
        select.appendChild(opt);
      });
      select.disabled = values.length === 0;
    }

    async loadInitial(keepSelection) {
      const prevHersteller = keepSelection && this.selHersteller ? this.selHersteller.value : '';
      const prevModell = keepSelection && this.selModell ? this.selModell.value : '';
      this.setBusy(true);
      try {
        // Beim ersten Laden auf einer Kategorieseite die aktiven Filter der aktuellen URL berücksichtigen
        const params = {};
        if (!keepSelection && this.dataset.contextHandle) {
          const sp = new URLSearchParams(window.location.search);
          const h = sp.get(PARAMS.hersteller);
          const m = sp.get(PARAMS.modell);
          if (h) params[PARAMS.hersteller] = h;
          if (m) params[PARAMS.modell] = m;
        } else if (prevHersteller) {
          params[PARAMS.hersteller] = prevHersteller;
        }
        const data = await this.fetchData(params);
        this.data = data;
        if (!data.available) {
          this.showFallback('Filter für Hersteller/Modell sind noch nicht eingerichtet.');
          return;
        }
        this.showMain();
        const herstellerValues = (data.hersteller && data.hersteller.values) || [];
        this.fillSelect(this.selHersteller, herstellerValues, 'Alle Hersteller', prevHersteller || undefined);
        const activeH = herstellerValues.find((v) => v.active);
        const chosenH = this.selHersteller.value || (activeH ? activeH.value : '');
        if (chosenH) {
          this.selHersteller.value = chosenH;
          await this.loadModelle(chosenH, prevModell);
        } else {
          this.fillSelect(this.selModell, [], 'Erst Hersteller wählen');
        }
        this.updateChips(herstellerValues);
        if (this.hsnForm) this.hsnForm.hidden = false;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        console.warn('[Fahrzeug-Finder]', err);
        this.showFallback('Fahrzeugdaten konnten nicht geladen werden.');
      } finally {
        this.setBusy(false);
      }
    }

    async loadModelle(hersteller, preselect) {
      this.setBusy(true);
      try {
        const data = await this.fetchData({ [PARAMS.hersteller]: hersteller });
        const values = ((data.modell && data.modell.values) || []).filter((v) => v.count > 0 || v.active);
        this.fillSelect(this.selModell, values, values.length ? 'Alle Modelle' : 'Keine Modelle hinterlegt', preselect || undefined);
        if (preselect && values.some((v) => v.value === preselect)) this.selModell.value = preselect;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        this.fillSelect(this.selModell, [], 'Alle Modelle');
        this.selModell.disabled = false;
      } finally {
        this.setBusy(false);
      }
    }

    onHerstellerChange() {
      const h = this.selHersteller.value;
      if (!h) {
        this.fillSelect(this.selModell, [], 'Erst Hersteller wählen');
        return;
      }
      this.loadModelle(h);
    }

    updateChips(values) {
      if (!this.chips.length) return;
      const known = new Set(values.map((v) => String(v.value).toLowerCase()));
      let visible = 0;
      this.chips.forEach((chip) => {
        const val = (chip.dataset.keMarke || '').toLowerCase();
        const show = this.designMode || known.has(val);
        chip.hidden = !show;
        if (show) visible += 1;
      });
      const wrap = this.querySelector('[data-ke-chips]');
      if (wrap) wrap.hidden = visible === 0;
    }

    showFallback(msg) {
      if (this.mainUi) this.mainUi.hidden = true;
      if (this.fallback) this.fallback.hidden = false;
      const intro = this.querySelector('.ke-finder__text');
      if (intro && !this.designMode) intro.innerHTML = '<p>Geben Sie Fahrzeugmodell, Baureihe oder OE-Nummer ein.</p>';
      if (this.hint) {
        this.hint.hidden = !this.designMode;
        if (this.designMode && msg) this.hint.textContent = `${msg} Lege in der App „Search & Discovery“ Filter für die Metafelder „Fahrzeughersteller“, „Modell / Baureihe“ und „Schlüsselnummer (HSN/TSN)“ an – dann füllen sich die Auswahllisten automatisch.`;
      }
      if (this.hsnForm) this.hsnForm.hidden = true;
    }

    showMain() {
      if (this.mainUi) this.mainUi.hidden = false;
      if (this.fallback) this.fallback.hidden = true;
      if (this.hint) this.hint.hidden = true;
    }

    buildUrl(base, params) {
      const parts = Object.entries(params)
        .filter(([, v]) => v)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
      return parts.length ? `${base}?${parts.join('&')}` : base;
    }

    onSubmit(e) {
      e.preventDefault();
      const params = {};
      if (this.selHersteller && this.selHersteller.value) params[PARAMS.hersteller] = this.selHersteller.value;
      if (this.selModell && this.selModell.value) params[PARAMS.modell] = this.selModell.value;
      window.location.assign(this.buildUrl(this.baseUrl, params));
    }

    onHsnSubmit(e) {
      e.preventDefault();
      const hsnInput = this.hsnForm.querySelector('[name="hsn"]');
      const tsnInput = this.hsnForm.querySelector('[name="tsn"]');
      let hsn = (hsnInput.value || '').replace(/\D/g, '');
      let tsn = (tsnInput.value || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
      if (!hsn && !tsn) {
        hsnInput.focus();
        return;
      }
      if (hsn && hsn.length < 4) hsn = hsn.padStart(4, '0');
      const code = tsn ? `${hsn}/${tsn.slice(0, 3)}` : hsn;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      if (this.data && this.data.hsn && tsn) {
        window.location.assign(this.buildUrl(`${root}collections/${this.allHandle}`, { [PARAMS.hsn]: code }));
      } else {
        // Fallback: Volltextsuche (Schlüsselnummer als Tag oder im Text)
        window.location.assign(`${root}search?type=product&q=${encodeURIComponent(code)}`);
      }
    }
  }

  if (!customElements.get('ke-fahrzeug-finder')) {
    customElements.define('ke-fahrzeug-finder', KeFahrzeugFinder);
  }
})();
